import React, { useState, useRef, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import useApi from '../hooks/useApi';
import haxios from '../lib/haxios';
import MessageBubble from './Messenger/shared/MessageBubble';
import { groupMessagesByDay, MessageDateSeparator } from './Messenger/shared/MessageDateGroup';
import MessageInput from './Messenger/shared/MessageInput';

const MessageDialog = ({
    isOpen,
    onClose,
    accountId,
    garageName,
    operatorData
}) => {
    const [newMessage, setNewMessage] = useState('');
    const [isSending, setIsSending] = useState(false);
    const messagesEndRef = useRef(null);

    const { data: messages, isLoading, refetch } = useApi({
        url: `/visiondashboard/messages?acct=${accountId}`,

    });

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    // Filter and group messages by day
    const filteredMessages = (messages?.data || []).filter(message => message.Acct === accountId);
    const sortedDays = groupMessagesByDay(filteredMessages);

    useEffect(() => {
        if (sortedDays.length > 0) {
            setTimeout(() => scrollToBottom(), 100);
        }
    }, [sortedDays]);



    const handleSend = async () => {
        if (!newMessage.trim()) return;

        try {
            setIsSending(true);
            await haxios.post('/visiondashboard/messages/add', {
                message: newMessage,
                acct: accountId,
                op: operatorData.Inits
            });
            setNewMessage('');
            refetch();
        } catch (error) {
            console.error('Failed to send message:', error);
        } finally {
            setIsSending(false);
        }
    };


    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg w-full max-w-[500px] h-[600px] flex flex-col">
                {/* Header */}
                <div className="p-4 border-b flex justify-between items-center">
                    <h2 className="text-lg font-semibold">{garageName} - Messages</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700"
                    >
                        ×
                    </button>
                </div>

                {/* Messages Area */}
                <div className="flex-1 overflow-y-auto p-4">
                    {isLoading ? (
                        <div className="flex justify-center items-center h-full">
                            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                        </div>
                    ) : sortedDays.length === 0 ? (
                        <div className="flex justify-center items-center h-full text-gray-500">
                            No messages yet. Start the conversation!
                        </div>
                    ) : (
                        sortedDays.map(({ dateDisplay, messages }) => (
                            <div key={dateDisplay} className="mb-6">
                                <MessageDateSeparator dateDisplay={dateDisplay} />
                                {messages.map(message => (
                                    <MessageBubble
                                        key={message.id}
                                        message={message}
                                        isCurrentUser={message.Op === operatorData.Inits}
                                        userName={message.Op}
                                    />
                                ))}
                            </div>
                        ))
                    )}
                    <div ref={messagesEndRef} />
                </div>

                {/* Input Area */}
                <div className="p-4 border-t">
                    <MessageInput
                        value={newMessage}
                        onChange={setNewMessage}
                        onSend={handleSend}
                        isSending={isSending}
                        placeholder="Type your message..."
                        inputType="input"
                    />
                </div>
            </div>
        </div>
    );
};

export default MessageDialog;